import {
  User, Phone, Mail, Briefcase, MessageSquare, Search, ChevronDown,
} from "lucide-react";

function Field({ label, icon: Icon, required, iconAlign = "center", children }) {
  const iconClass =
    iconAlign === "top"
      ? "absolute left-3 top-3 text-gray-400 pointer-events-none"
      : "absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none";
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      <div className="relative">
        {Icon && <Icon size={18} className={iconClass} />}
        {children}
      </div>
    </label>
  );
}

const inputClass =
  "w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg " +
  "text-gray-900 placeholder-gray-400 " +
  "focus:outline-none focus:ring-2 focus:ring-offset-0 " +
  "transition-shadow";

const selectClass =
  "w-full pl-10 pr-9 py-2.5 border border-gray-300 rounded-lg appearance-none " +
  "text-gray-900 bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-offset-0 " +
  "transition-shadow";

function ChoiceField({ label, icon, options, value, onChange, focusStyle, placeholder }) {
  return (
    <Field label={label} icon={icon}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={selectClass}
        style={focusStyle}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <ChevronDown
        size={16}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      />
    </Field>
  );
}

export default function PublicLeadFields({ form, values, onChange, focusColor }) {
  const update = (k) => (e) => onChange({ ...values, [k]: e.target.value });

  const focusStyle = { "--tw-ring-color": focusColor || "#10b981" };
  const servicioOptions = Array.isArray(form.servicio_options) ? form.servicio_options : [];
  const conocioOptions = Array.isArray(form.conocio_options) ? form.conocio_options : [];

  return (
    <div className="space-y-4">
      <Field label="Nombre" icon={User} required>
        <input
          type="text"
          required
          maxLength={100}
          value={values.nombre}
          onChange={update("nombre")}
          className={inputClass}
          style={focusStyle}
          placeholder="Tu nombre"
          autoComplete="name"
        />
      </Field>

      <Field
        label="Teléfono"
        icon={Phone}
        required={form.require_phone}
      >
        <input
          type="tel"
          required={form.require_phone}
          maxLength={30}
          value={values.telefono}
          onChange={update("telefono")}
          className={inputClass}
          style={focusStyle}
          placeholder="+34 600 000 000"
          autoComplete="tel"
        />
      </Field>

      <Field label="Email" icon={Mail} required={form.require_email}>
        <input
          type="email"
          required={form.require_email}
          maxLength={150}
          value={values.email}
          onChange={update("email")}
          className={inputClass}
          style={focusStyle}
          placeholder="tu@email.com"
          autoComplete="email"
        />
      </Field>

      {form.show_servicio && (
        servicioOptions.length > 0 ? (
          <ChoiceField
            label="¿Qué servicio te interesa?"
            icon={Briefcase}
            options={servicioOptions}
            value={values.servicio}
            onChange={(v) => onChange({ ...values, servicio: v })}
            focusStyle={focusStyle}
            placeholder="Selecciona una opción"
          />
        ) : (
          <Field label="¿Qué servicio te interesa?" icon={Briefcase}>
            <input
              type="text"
              maxLength={80}
              value={values.servicio}
              onChange={update("servicio")}
              className={inputClass}
              style={focusStyle}
              placeholder="Personal, grupal, online..."
            />
          </Field>
        )
      )}

      {form.show_como_conocio && (
        conocioOptions.length > 0 ? (
          <ChoiceField
            label="¿Cómo nos conociste?"
            icon={Search}
            options={conocioOptions}
            value={values.como_conocio}
            onChange={(v) => onChange({ ...values, como_conocio: v })}
            focusStyle={focusStyle}
            placeholder="Selecciona una opción"
          />
        ) : (
          <Field label="¿Cómo nos conociste?" icon={Search}>
            <input
              type="text"
              maxLength={80}
              value={values.como_conocio}
              onChange={update("como_conocio")}
              className={inputClass}
              style={focusStyle}
              placeholder="Instagram, Google, recomendación..."
            />
          </Field>
        )
      )}

      <Field label="Mensaje (opcional)" icon={MessageSquare} iconAlign="top">
        <textarea
          maxLength={500}
          rows={3}
          value={values.observaciones}
          onChange={update("observaciones")}
          className={inputClass + " resize-none"}
          style={focusStyle}
          placeholder="Cuéntanos qué buscas..."
        />
      </Field>
    </div>
  );
}
